/**
 * @file Recalls module Settings surface.
 */

import { appData, tempConfigData } from "../../app/state/state.js";
import { bindNativeFieldBehavior } from "../../shared/forms/nativeFieldStyles.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
    bindRecallsImportSettings,
    handleRecallsImportSettingsButtonClick,
    renderRecallsImportSettings,
} from "./recallsImportSettings.js";
import {
    getRecallProviderDisplayLabel,
} from "../recalls/recallsProviderUtils.js";
import {
    bindStatusPrioritySettingsControls,
    handleStatusPrioritySettingsButtonClick,
    renderOptionEditor,
} from "./statusPrioritySettings.js";
import { checkDataUsage } from "./settingsApi.js";

const RECALLS_SETTINGS_LAYOUT_CLASSES =
    "recalls-settings-layout w-full space-y-8";
const RECALLS_SETTINGS_CARD_CLASSES =
    "recalls-settings-card bg-white p-6 rounded-lg shadow-md";

const STATUS_ROLE_OPTIONS = Object.freeze([
    { key: "initial", label: "Initial" },
    { key: "emailed", label: "Emailed" },
    { key: "booked", label: "Booked" },
    { key: "rescheduled", label: "Rescheduled" },
    { key: "declined", label: "Declined" },
    { key: "cancelled", label: "Cancelled" },
]);

const REASON_LISTS = Object.freeze([
    { key: "recallReasons", label: "Recall reasons", addLabel: "Add Reason" },
    { key: "declinedReasons", label: "Declined reasons", addLabel: "Add Reason" },
    { key: "cancelledReasons", label: "Cancelled reasons", addLabel: "Add Reason" },
    { key: "rescheduleReasons", label: "Reschedule reasons", addLabel: "Add Reason" },
]);

let showInactiveReasons = false;
let showInactiveExportProviders = false;

function attrValue(value = "") {
    return escapeHTML(String(value ?? ""));
}

function normalizeReasonIds(value) {
    return (Array.isArray(value) ? value : [value])
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);
}

function getLoadedRecallRowsForUsage() {
    return [
        ...(Array.isArray(appData.recalls) ? appData.recalls : []),
        ...(Array.isArray(appData.closedRecalls) ? appData.closedRecalls : []),
    ];
}

function getLoadedUsedReasonIds(listKey) {
    const used = new Set();
    getLoadedRecallRowsForUsage().forEach((recall) => {
        if (listKey === "recallReasons") {
            normalizeReasonIds(recall.recall_reason)
                .forEach((reasonId) => used.add(reasonId));
        } else if (listKey === "declinedReasons" && recall.declinedReason) {
            used.add(String(recall.declinedReason));
        } else if (listKey === "cancelledReasons" && recall.cancelledReason) {
            used.add(String(recall.cancelledReason));
        } else if (listKey === "rescheduleReasons" &&
            recall.rescheduleReason) {
            used.add(String(recall.rescheduleReason));
        }
    });
    return used;
}

function syncLoadedReasonUsageFlags(config) {
    REASON_LISTS.forEach((list) => {
        const usedIds = getLoadedUsedReasonIds(list.key);
        if (usedIds.size === 0) {return;}
        config[list.key].forEach((reason) => {
            if (reason?.id && usedIds.has(String(reason.id))) {
                reason.hasBeenUsed = true;
            }
        });
    });
}

function getRecallsConfig() {
    if (!tempConfigData.recalls || typeof tempConfigData.recalls !== "object") {
        tempConfigData.recalls = {};
    }

    const config = tempConfigData.recalls;
    if (!config.statusRoleMappings ||
        typeof config.statusRoleMappings !== "object") {
        config.statusRoleMappings = {};
    }
    STATUS_ROLE_OPTIONS.forEach((role) => {
        if (!Object.prototype.hasOwnProperty.call(
            config.statusRoleMappings,
            role.key,
        )) {
            config.statusRoleMappings[role.key] = null;
        }
    });

    REASON_LISTS.forEach((list) => {
        if (!Array.isArray(config[list.key])) {
            config[list.key] = [];
        }
    });

    if (!config.exportReplacementText ||
        typeof config.exportReplacementText !== "object") {
        config.exportReplacementText = {};
    }
    if (!config.exportReplacementText.providers ||
        typeof config.exportReplacementText.providers !== "object") {
        config.exportReplacementText.providers = {};
    }
    if (!config.exportReplacementText.recallReasons ||
        typeof config.exportReplacementText.recallReasons !== "object") {
        config.exportReplacementText.recallReasons = {};
    }
    if (!Array.isArray(config.legacyProviders)) {
        config.legacyProviders = [];
    }
    syncLoadedReasonUsageFlags(config);
    if (
        config.nearDateWarningDays === null ||
        config.nearDateWarningDays === undefined ||
        config.nearDateWarningDays === "" ||
        !Number.isFinite(Number(config.nearDateWarningDays))
    ) {
        config.nearDateWarningDays = null;
    }
    if (
        config.recentlyClosedRecallWarningDays === null ||
        config.recentlyClosedRecallWarningDays === undefined ||
        config.recentlyClosedRecallWarningDays === "" ||
        !Number.isFinite(Number(config.recentlyClosedRecallWarningDays))
    ) {
        config.recentlyClosedRecallWarningDays = null;
    }

    return config;
}

function getSortedOptionEntries(options = []) {
    return options
        .map((option, index) => ({
            index,
            option,
            order: Number(option?.order ?? index + 1) || index + 1,
        }))
        .sort((a, b) => {
            const activeDiff = (a.option?.isActive === false ? 1 : 0) -
                (b.option?.isActive === false ? 1 : 0);
            if (activeDiff !== 0) {return activeDiff;}
            const orderDiff = a.order - b.order;
            return orderDiff !== 0 ? orderDiff : a.index - b.index;
        });
}

function getVisibleReasonEntries(options = []) {
    return getSortedOptionEntries(options)
        .filter((entry) => showInactiveReasons ||
            entry.option?.isActive !== false);
}

function getMaxActiveOrder(options = []) {
    return Math.max(
        0,
        ...options
            .filter((option) => option?.isActive !== false)
            .map((option) => Number(option.order) || 0),
    );
}

function moveReasonOption(listKey, index, direction) {
    const options = getRecallsConfig()[listKey];
    const target = options[index];
    if (!target || typeof target !== "object") {return false;}

    const targetIsActive = target.isActive !== false;
    const entries = options
        .map((option, optionIndex) => ({
            index: optionIndex,
            option,
            order: Number(option?.order ?? optionIndex + 1) || optionIndex + 1,
        }))
        .filter((entry) =>
            (entry.option?.isActive !== false) === targetIsActive)
        .sort((a, b) => {
            const orderDiff = a.order - b.order;
            return orderDiff !== 0 ? orderDiff : a.index - b.index;
        });
    const currentPosition = entries.findIndex((entry) => entry.index === index);
    const nextPosition = direction === "up"
        ? currentPosition - 1
        : currentPosition + 1;

    if (
        currentPosition < 0 ||
        nextPosition < 0 ||
        nextPosition >= entries.length
    ) {
        return false;
    }

    const current = entries[currentPosition].option;
    const next = entries[nextPosition].option;
    const currentOrder = Number(current.order) || entries[currentPosition].order;
    const nextOrder = Number(next.order) || entries[nextPosition].order;
    current.order = nextOrder;
    next.order = currentOrder;
    return true;
}

function getStatusOptions(selectedValue = "") {
    const selected = String(selectedValue || "");
    const statuses = Array.isArray(tempConfigData.recallStatus)
        ? [...tempConfigData.recallStatus]
        : [];
    const options = statuses
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .filter((status) => status?.id || String(status?.id) === selected)
        .map((status) => ({
            value: String(status.id),
            label: String(status.name || status.id),
            accentColor: status.color || "",
            searchText: `${status.name || ""} ${status.id || ""}`,
        }));

    if (
        selected &&
        !options.some((option) => String(option.value) === selected)
    ) {
        options.push({
            value: selected,
            label: `Saved status (${selected})`,
            selectedOnly: true,
            unavailableLabel: "missing",
        });
    }

    return options;
}

function renderOrderButton({
    className,
    direction,
    disabled,
    index,
    listKey,
}) {
    const icon = direction === "up" ? "expand_less" : "expand_more";
    const label = direction === "up" ? "Move option up" : "Move option down";
    const disabledAttr = disabled ? " disabled" : "";
    return `
        <button type="button" class="${className} inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-50 hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)] disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-gray-400" data-list-key="${attrValue(listKey)}" data-index="${index}" data-direction="${direction}" title="${label}" aria-label="${label}"${disabledAttr}>
            <span class="material-symbols-outlined pointer-events-none text-[18px]">${icon}</span>
        </button>
    `;
}

function renderReasonAction(entry, listKey) {
    const isActive = entry.option?.isActive !== false;
    const hasBeenUsed = entry.option?.hasBeenUsed === true;

    if (!hasBeenUsed) {
        return `
            <button type="button" class="delete-recalls-reason-btn settings-config-delete" data-list-key="${attrValue(listKey)}" data-index="${entry.index}" title="Delete reason" aria-label="Delete reason">
                <span class="material-symbols-outlined text-lg">delete</span>
            </button>
        `;
    }

    return `
        <button type="button" class="archive-recalls-reason-btn settings-config-archive ${isActive ? "" : "settings-config-restore"}" data-list-key="${attrValue(listKey)}" data-index="${entry.index}" title="${isActive ? "Archive reason" : "Restore reason"}" aria-label="${isActive ? "Archive reason" : "Restore reason"}">
            <span class="material-symbols-outlined text-lg">${isActive ? "archive" : "unarchive"}</span>
        </button>
    `;
}

function renderStatusRoleMappings() {
    return `
        <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            ${STATUS_ROLE_OPTIONS.map((role) => `
                <div>
                    <label class="block font-medium mb-2">${role.label}</label>
                    <div data-recalls-status-role-control="${role.key}"></div>
                </div>
            `).join("")}
        </div>
    `;
}

function renderReasonList(listConfig) {
    const config = getRecallsConfig();
    const entries = getVisibleReasonEntries(config[listConfig.key]);
    return `
        <div>
            <label class="block font-medium mb-2">${listConfig.label}</label>
            <div class="recalls-reason-list">
                ${entries.map((entry) => {
        const isActive = entry.option?.isActive !== false;
        const sameStateEntries = entries.filter((item) =>
            (item.option?.isActive !== false) === isActive);
        const stateIndex = sameStateEntries.findIndex((item) =>
            item.index === entry.index);
        const isFirst = stateIndex === 0;
        const isLast = stateIndex === sameStateEntries.length - 1;
        const disabledAttr = isActive ? "" : " disabled";
        return `
            <div class="recalls-reason-row ${isActive ? "" : "opacity-75"}">
                <div class="settings-config-order-controls recalls-reason-order" aria-label="${attrValue(listConfig.label)} order controls">
                    ${renderOrderButton({
                        className: "move-recalls-reason-btn recalls-reason-move-btn",
                        direction: "up",
                        disabled: isFirst,
                        index: entry.index,
                        listKey: listConfig.key,
                    })}
                    ${renderOrderButton({
                        className: "move-recalls-reason-btn recalls-reason-move-btn",
                        direction: "down",
                        disabled: isLast,
                        index: entry.index,
                        listKey: listConfig.key,
                    })}
                </div>
                <input type="text" value="${attrValue(entry.option?.name || "")}" data-recalls-reason-list="${attrValue(listConfig.key)}" data-index="${entry.index}" class="settings-config-name recalls-reason-name" placeholder="Reason"${disabledAttr}>
                <div class="recalls-reason-action">
                    ${renderReasonAction(entry, listConfig.key)}
                </div>
            </div>
        `;
    }).join("")}
            </div>
            <button type="button" class="add-recalls-reason-btn settings-add-row-btn mt-3" data-list-key="${attrValue(listConfig.key)}">+ ${listConfig.addLabel}</button>
        </div>
    `;
}

function getMappedStatusRoleLabels(statusId) {
    const id = String(statusId || "");
    if (!id) {return [];}
    const mappings = getRecallsConfig().statusRoleMappings || {};
    return STATUS_ROLE_OPTIONS
        .filter((role) => String(mappings[role.key] || "") === id)
        .map((role) => role.label);
}

async function handleRecallStatusDelete(btn, {
    rerenderCurrentTabContent,
    updateSaveButton,
}) {
    const index = parseInt(btn.dataset.index, 10);
    const status = tempConfigData.recallStatus?.[index];
    if (!status || typeof status !== "object") {return true;}
    const statusId = String(status.id || "");
    const mappedRoles = getMappedStatusRoleLabels(statusId);
    if (mappedRoles.length > 0) {
        openConfirmationModal(
            "Cannot Delete Workflow Status",
            `This status is mapped to ${mappedRoles.join(", ")}. Change the workflow mapping before deleting it.`,
            () => {},
            false,
            "OK",
        );
        return true;
    }

    const originalIcon = btn.innerHTML;
    btn.innerHTML = '<span class="material-symbols-outlined text-lg animate-spin">progress_activity</span>';
    btn.disabled = true;
    const usageCount = await checkDataUsage("recall", statusId);
    btn.innerHTML = originalIcon;
    btn.disabled = false;

    if (usageCount > 0) {
        openConfirmationModal(
            "Cannot Delete Status",
            `This status is currently used by <strong>${usageCount} Recalls</strong> in the database.<br><br>Move those Recalls to a different status before deleting this option.`,
            () => {},
            false,
            "OK",
        );
        return true;
    }
    if (usageCount === -1) {
        openConfirmationModal(
            "Error",
            "Could not verify usage on server. Please try again or check your connection.",
            () => {},
            false,
            "OK",
        );
        return true;
    }

    openConfirmationModal(
        "Delete Status?",
        "Delete this unused Recalls status from Settings?",
        () => {
            tempConfigData.recallStatus.splice(index, 1);
            updateSaveButton(true);
            rerenderCurrentTabContent();
        },
    );
    return true;
}

function getAppProviderEntries() {
    return (appData.users || [])
        .filter((user) => user?.isAppProvider === true)
        .map((user) => ({
            key: user.id || user.uid,
            label: user.name || user.email || user.id || user.uid,
            isActive: user.isActive !== false,
            type: "app",
        }))
        .filter((provider) => provider.key)
        .sort((a, b) => {
            const activeDiff = (a.isActive === false ? 1 : 0) -
                (b.isActive === false ? 1 : 0);
            if (activeDiff !== 0) {return activeDiff;}
            return (a.label || "").localeCompare(b.label || "");
        });
}

function getLegacyProviderEntries() {
    return getRecallsConfig().legacyProviders
        .map((provider) => ({
            key: provider?.key,
            label: getRecallProviderDisplayLabel({
                type: "legacy",
                legacyKey: provider?.key,
                label: provider?.label || provider?.key,
            }),
            isActive: false,
            type: "legacy",
        }))
        .filter((provider) => provider.key)
        .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
}

function renderProviderReplacementRows() {
    const config = getRecallsConfig();
    const providers = [
        ...getAppProviderEntries(),
        ...getLegacyProviderEntries(),
    ].filter((provider) => showInactiveExportProviders ||
        (provider.type === "app" && provider.isActive !== false));
    if (providers.length === 0) {
        return `
            <p class="settings-helper-text">No active app providers are available yet.</p>
        `;
    }

    return `
        <div class="space-y-2">
            ${providers.map((provider) => `
                <div class="settings-config-row ${provider.isActive ? "" : "opacity-75"}">
                    <div class="min-w-0 flex-1">
                        <div class="truncate text-sm font-medium text-gray-800">${escapeHTML(provider.label || provider.key)}</div>
                        <div class="text-xs text-gray-500">${provider.type === "legacy" ? "Legacy import value" : (provider.isActive ? "App provider" : "Inactive app provider")}</div>
                    </div>
                    <input type="text" value="${attrValue(config.exportReplacementText.providers[provider.key] || "")}" data-recalls-provider-replacement="${attrValue(provider.key)}" class="settings-config-name max-w-sm" placeholder="Export text">
                </div>
            `).join("")}
        </div>
    `;
}

function renderRecallReasonReplacementRows() {
    const config = getRecallsConfig();
    const reasons = getSortedOptionEntries(config.recallReasons);
    if (reasons.length === 0) {
        return `
            <p class="settings-helper-text">Add recall reasons before configuring export replacement text.</p>
        `;
    }

    return `
        <div class="space-y-2">
            ${reasons.map((entry) => {
        const reason = entry.option || {};
        return `
            <div class="settings-config-row ${reason.isActive === false ? "opacity-75" : ""}">
                <div class="min-w-0 flex-1">
                    <div class="truncate text-sm font-medium text-gray-800">${escapeHTML(reason.name || reason.id || "Unnamed reason")}</div>
                    <div class="text-xs text-gray-500">Recall reason</div>
                </div>
                <input type="text" value="${attrValue(config.exportReplacementText.recallReasons[reason.id] || "")}" data-recalls-reason-replacement="${attrValue(reason.id || "")}" class="settings-config-name max-w-sm" placeholder="Export text">
            </div>
        `;
    }).join("")}
        </div>
    `;
}

function renderNearDateSettings() {
    const config = getRecallsConfig();
    const nearDateValue = config.nearDateWarningDays;
    const recentClosedValue = config.recentlyClosedRecallWarningDays;
    return `
        <div class="grid max-w-3xl gap-4 md:grid-cols-2">
            <div>
                <label class="block font-medium mb-2">Near-date warning days</label>
                <input type="number" min="0" step="1" value="${Number.isFinite(Number(nearDateValue)) ? Number(nearDateValue) : ""}" data-recalls-near-date-warning-days class="settings-config-name" placeholder="No warning">
            </div>
            <div>
                <label class="block font-medium mb-2">Recently closed warning days</label>
                <input type="number" min="0" step="1" value="${Number.isFinite(Number(recentClosedValue)) ? Number(recentClosedValue) : ""}" data-recalls-recently-closed-warning-days class="settings-config-name" placeholder="No warning">
            </div>
        </div>
    `;
}

function bindStatusRoleControls(container, { updateSaveButton = () => {} }) {
    const config = getRecallsConfig();
    STATUS_ROLE_OPTIONS.forEach((role) => {
        const root = container.querySelector(
            `[data-recalls-status-role-control="${role.key}"]`,
        );
        if (!root) {return;}
        const selected = config.statusRoleMappings[role.key] || "";
        createSelectControl({
            root,
            accessibleName: `${role.label} status mapping`,
            value: selected,
            options: getStatusOptions(selected),
            clearable: true,
            searchable: true,
            searchThreshold: 7,
            portalMenu: true,
            placeholder: "Select status",
            mirror: false,
            onChange: (value) => {
                config.statusRoleMappings[role.key] = value || null;
                updateSaveButton(true);
            },
        });
    });
}

/**
 * Renders the Recalls Settings tab.
 * @param {HTMLElement} container Settings tab content container.
 * @param {{ updateSaveButton?: Function }} [options={}] Render dependencies.
 * @returns {void}
 */
export function renderRecallsSettingsTab(container, options = {}) {
    const { updateSaveButton = () => {} } = options;
    if (!Array.isArray(tempConfigData.recallStatus)) {
        tempConfigData.recallStatus = [];
    }
    getRecallsConfig();

    container.innerHTML = `
        <div class="${RECALLS_SETTINGS_LAYOUT_CLASSES}">
            <div class="${RECALLS_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Recalls Statuses</h4>
                ${renderOptionEditor("Status Options", "recallStatus", tempConfigData.recallStatus, true)}
            </div>
            <div class="${RECALLS_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Status Workflow Mappings</h4>
                ${renderStatusRoleMappings()}
            </div>
            <div class="${RECALLS_SETTINGS_CARD_CLASSES}">
                <div class="mb-4 flex flex-wrap items-center justify-between gap-3 border-b pb-2">
                    <h4 class="text-lg font-semibold">Reasons</h4>
                    <label class="inline-flex items-center gap-2 text-xs text-gray-600">
                        <input type="checkbox" data-recalls-show-inactive-reasons ${showInactiveReasons ? "checked" : ""}>
                        <span>Show inactive</span>
                    </label>
                </div>
                <div class="grid gap-8 xl:grid-cols-2">
                    ${REASON_LISTS.map((listConfig) => renderReasonList(listConfig)).join("")}
                </div>
            </div>
            <div class="${RECALLS_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Operational Guardrails</h4>
                ${renderNearDateSettings()}
            </div>
            <div class="${RECALLS_SETTINGS_CARD_CLASSES}">
                <h4 class="text-lg font-semibold mb-4 border-b pb-2">Marketing Export Text</h4>
                <div class="grid gap-8 xl:grid-cols-2">
                    <div>
                        <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
                            <h5 class="text-sm font-semibold text-gray-900">Providers</h5>
                            <label class="inline-flex items-center gap-2 text-xs text-gray-600">
                                <input type="checkbox" data-recalls-show-inactive-export-providers ${showInactiveExportProviders ? "checked" : ""}>
                                <span>Show inactive/legacy</span>
                            </label>
                        </div>
                        ${renderProviderReplacementRows()}
                    </div>
                    <div>
                        <h5 class="text-sm font-semibold text-gray-900 mb-3">Recall reasons</h5>
                        ${renderRecallReasonReplacementRows()}
                    </div>
                </div>
            </div>
            <div class="${RECALLS_SETTINGS_CARD_CLASSES}">
                ${renderRecallsImportSettings()}
            </div>
        </div>
    `;

    bindStatusPrioritySettingsControls(container);
    bindStatusRoleControls(container, { updateSaveButton });
    bindRecallsImportSettings(container, {
        rerenderCurrentTabContent: () =>
            renderRecallsSettingsTab(container, options),
    });
    bindNativeFieldBehavior(container);
}

/**
 * Handles Recalls Settings input changes.
 * @param {HTMLElement} target Event target.
 * @param {{ updateSaveButton?: Function, rerenderCurrentTabContent?: Function }} [options={}] Handler dependencies.
 * @returns {boolean} True when handled.
 */
export function handleRecallsSettingsChange(target, options = {}) {
    const { updateSaveButton = () => {} } = options;
    const config = getRecallsConfig();

    if (target.matches("[data-recalls-reason-list]")) {
        const listKey = target.dataset.recallsReasonList;
        const index = parseInt(target.dataset.index, 10);
        const reason = config[listKey]?.[index];
        if (!reason || reason.isActive === false) {return true;}
        reason.name = target.value;
        updateSaveButton(true);
        return true;
    }

    if (target.matches("[data-recalls-near-date-warning-days]")) {
        const rawValue = String(target.value || "").trim();
        config.nearDateWarningDays = rawValue === ""
            ? null
            : Math.max(0, Math.floor(Number(rawValue) || 0));
        updateSaveButton(true);
        return true;
    }

    if (target.matches("[data-recalls-recently-closed-warning-days]")) {
        const rawValue = String(target.value || "").trim();
        config.recentlyClosedRecallWarningDays = rawValue === ""
            ? null
            : Math.max(0, Math.floor(Number(rawValue) || 0));
        updateSaveButton(true);
        return true;
    }

    if (target.matches("[data-recalls-show-inactive-reasons]")) {
        showInactiveReasons = target.checked;
        options.rerenderCurrentTabContent?.();
        return true;
    }

    if (target.matches("[data-recalls-show-inactive-export-providers]")) {
        showInactiveExportProviders = target.checked;
        options.rerenderCurrentTabContent?.();
        return true;
    }

    if (target.matches("[data-recalls-provider-replacement]")) {
        const key = target.dataset.recallsProviderReplacement;
        config.exportReplacementText.providers[key] =
            target.value.trim() || "";
        updateSaveButton(true);
        return true;
    }

    if (target.matches("[data-recalls-reason-replacement]")) {
        const key = target.dataset.recallsReasonReplacement;
        if (!key) {return true;}
        config.exportReplacementText.recallReasons[key] =
            target.value.trim() || "";
        updateSaveButton(true);
        return true;
    }

    return false;
}

/**
 * Handles Recalls Settings button actions.
 * @param {HTMLElement} target Event target.
 * @param {{ updateSaveButton?: Function, rerenderCurrentTabContent?: Function }} [options={}] Handler dependencies.
 * @returns {Promise<boolean>} True when handled.
 */
export async function handleRecallsSettingsButtonClick(target, options = {}) {
    const {
        rerenderCurrentTabContent = () => {},
        updateSaveButton = () => {},
    } = options;
    const config = getRecallsConfig();

    if (await handleRecallsImportSettingsButtonClick(target, {
        rerenderCurrentTabContent,
    })) {
        return true;
    }

    const recallStatusDelete = target.closest(
        ".delete-option-btn[data-key=\"recallStatus\"]",
    );
    if (recallStatusDelete) {
        return handleRecallStatusDelete(recallStatusDelete, {
            rerenderCurrentTabContent,
            updateSaveButton,
        });
    }

    if (await handleStatusPrioritySettingsButtonClick(target, {
        updateSaveButton,
        rerenderCurrentTabContent,
    })) {
        return true;
    }

    if (target.closest(".add-recalls-reason-btn")) {
        const listKey = target.closest(".add-recalls-reason-btn").dataset.listKey;
        const list = config[listKey];
        if (!Array.isArray(list)) {return true;}
        list.push({
            id: `reason_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
            name: "New Reason",
            isActive: true,
            order: getMaxActiveOrder(list) + 1,
            hasBeenUsed: false,
        });
        updateSaveButton(true);
        rerenderCurrentTabContent();
        return true;
    }

    if (target.closest(".move-recalls-reason-btn")) {
        const btn = target.closest(".move-recalls-reason-btn");
        if (moveReasonOption(
            btn.dataset.listKey,
            parseInt(btn.dataset.index, 10),
            btn.dataset.direction,
        )) {
            updateSaveButton(true);
            rerenderCurrentTabContent();
        }
        return true;
    }

    if (target.closest(".archive-recalls-reason-btn")) {
        const btn = target.closest(".archive-recalls-reason-btn");
        const reason = config[btn.dataset.listKey]?.[
            parseInt(btn.dataset.index, 10)
        ];
        if (!reason || typeof reason !== "object") {return true;}
        if (reason.hasBeenUsed !== true) {return true;}
        reason.isActive = reason.isActive === false;
        if (reason.isActive) {
            reason.order = getMaxActiveOrder(config[btn.dataset.listKey]) + 1;
        }
        updateSaveButton(true);
        rerenderCurrentTabContent();
        return true;
    }

    if (target.closest(".delete-recalls-reason-btn")) {
        const btn = target.closest(".delete-recalls-reason-btn");
        const listKey = btn.dataset.listKey;
        const index = parseInt(btn.dataset.index, 10);
        const reason = config[listKey]?.[index];
        if (!reason || typeof reason !== "object") {return true;}
        if (reason.hasBeenUsed === true) {return true;}

        openConfirmationModal(
            "Delete Reason?",
            "Delete this unused Recalls reason from Settings?",
            () => {
                config[listKey].splice(index, 1);
                updateSaveButton(true);
                rerenderCurrentTabContent();
            },
        );
        return true;
    }

    return false;
}

/**
 * Validates Recalls Settings before the shared Settings save.
 * @returns {boolean} True when the Recalls Settings draft can be saved.
 */
export function validateRecallsSettingsDraft() {
    const mappings = getRecallsConfig().statusRoleMappings || {};
    const used = new Map();

    for (const role of STATUS_ROLE_OPTIONS) {
        const statusId = String(mappings[role.key] || "").trim();
        if (!statusId) {continue;}
        if (used.has(statusId)) {
            const firstRole = used.get(statusId);
            openConfirmationModal(
                "Duplicate Recalls Status Mapping",
                `${firstRole} and ${role.label} cannot use the same configured status.`,
                () => {},
                false,
                "OK",
            );
            return false;
        }
        used.set(statusId, role.label);
    }

    return true;
}
